import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { CalendarCheck, CheckCircle2, ArrowRight, Info, Phone, Calendar as CalendarIcon } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

const motorTypes = [
  "Crossmotor", "Enduromotor", "Baanmotor", "Naked bike", "Sportmotor",
  "Touringmotor", "Adventuremotor", "Supermoto", "Scooter", "Andere",
];
const services = [
  "Onderhoud", "Herstelling", "Diagnose", "Banden vervangen", "Remmen",
  "Ophanging", "Elektrische storing", "Onderdelen monteren", "Keuringklaar maken",
  "Aankoopcontrole", "Schadeherstelling", "Andere",
];

const DAY_INDEX = { zondag: 0, maandag: 1, dinsdag: 2, woensdag: 3, donderdag: 4, vrijdag: 5, zaterdag: 6 };

const tijdstippen = ["Voormiddag (9:00 - 12:00)", "Namiddag (13:00 - 16:00)", "Einde dag (16:00 - 18:30)"];

const DEFAULT_HOURS = [
  { dag: "Maandag", uren: "09:00 - 12:00 / 13:00 - 18:30" },
  { dag: "Dinsdag", uren: "09:00 - 12:00 / 13:00 - 18:30" },
  { dag: "Woensdag", uren: "09:00 - 12:00 / 13:00 - 18:30" },
  { dag: "Donderdag", uren: "09:00 - 12:00 / 13:00 - 18:30" },
  { dag: "Vrijdag", uren: "09:00 - 12:00 / 13:00 - 18:30" },
  { dag: "Zaterdag", uren: "09:00 - 12:00" },
  { dag: "Zondag", uren: "Gesloten" },
];

// Parse opening-hour string like "09:00 - 12:00 / 13:00 - 18:30" into 30-min appointment slots
function slotsForUren(uren, step = 30) {
  if (!uren || /gesloten/i.test(uren)) return [];
  const out = [];
  uren.split(/[\/;]| en /i).forEach((part) => {
    const m = part.match(/(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})/);
    if (!m) return;
    const start = +m[1] * 60 + +m[2];
    const end = +m[3] * 60 + +m[4];
    for (let t = start; t + step <= end; t += step) {
      out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    }
  });
  return out;
}
export default function Afspraak() {
  useSEO({
    title: "Online afspraak maken — motoronderhoud & herstelling Torhout | TT Motorsports",
    description: "Vraag online een afspraak aan bij TT Motorsports in Torhout voor motoronderhoud, herstelling, diagnose, banden, remmen en meer. Voor alle merken en alle types motoren.",
    keywords: "afspraak motoronderhoud, motor herstelling afspraak, motor garage Torhout, online afspraak motor, TT Motorsports",
  });

  const init = {
    naam: "", email: "", telefoon: "", type_motor: "", merk: "", model: "", bouwjaar: "",
    nummerplaat: "", kilometerstand: "", service: "", service_andere: "",
    datum: "", tijdstip: "", wacht: false, ophalen: false,
    ophaal_straat: "", ophaal_postcode: "", ophaal_stad: "",
    opmerkingen: "", honeypot: "",
  };
  const [f, setF] = useState(init);
  const [status, setStatus] = useState("idle");
  const [settings, setSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const openingsuren = settings?.openingsuren?.length ? settings.openingsuren : DEFAULT_HOURS;
  const verlofdagen = settings?.verlofdagen || [];
  const verlofperiodes = settings?.verlofperiodes || [];

  const openDays = new Set();
  const hoursMap = {};
  openingsuren.forEach((o) => {
    const idx = DAY_INDEX[(o.dag || "").trim().toLowerCase()];
    if (idx === undefined) return;
    if (o.uren && !/gesloten/i.test(o.uren)) { openDays.add(idx); hoursMap[idx] = o.uren; }
  });

  const isVerlof = (date) => {
    const iso = format(date, "yyyy-MM-dd");
    if (verlofdagen.includes(iso)) return true;
    return verlofperiodes.some((p) => p.van && p.tot && p.van <= iso && iso <= p.tot);
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Beschikbare slots via backend (houdt rekening met duur, pauzes, verlof, blokken en capaciteit)
  useEffect(() => {
    if (!f.datum || !f.service) { setAvailableSlots([]); return; }
    setLoadingSlots(true);
    api.get("/availability", { params: { datum: f.datum, service: f.service, wacht: f.wacht } })
      .then((r) => setAvailableSlots(r.data?.slots || []))
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [f.datum, f.service, f.wacht]);

  useEffect(() => {
    if (f.tijdstip && !availableSlots.includes(f.tijdstip)) set("tijdstip", "");
  }, [availableSlots]); // eslint-disable-line

  const pickDate = (d) => {
    setSelectedDate(d || null);
    setCalOpen(false);
    set("datum", d ? format(d, "yyyy-MM-dd") : "");
    set("tijdstip", "");
  };

  const refreshSlots = () => {
    if (f.datum && f.service) api.get("/availability", { params: { datum: f.datum, service: f.service, wacht: f.wacht } })
      .then((r) => setAvailableSlots(r.data?.slots || [])).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.naam || !f.email || !f.telefoon || !f.service) return;
    if (f.service === "Andere" && !f.service_andere.trim()) return;
    if (f.ophalen && (!f.ophaal_straat.trim() || !f.ophaal_postcode.trim() || !f.ophaal_stad.trim())) {
      setStatus("error");
      setErrMsg("Vul je volledige ophaaladres in (straat, postcode en stad).");
      return;
    }
    setStatus("sending"); setErrMsg("");
    try {
      await api.post("/appointments", f);
      setStatus("done");
      window.scrollTo(0, 0);
    } catch (err) {
      if (err?.response?.status === 409) {
        setErrMsg("Dit tijdstip is net bezet geraakt. Kies een ander tijdstip.");
        set("tijdstip", "");
        refreshSlots();
      } else {
        setErrMsg("Er ging iets mis. Probeer opnieuw of bel ons op 0485 74 15 24.");
      }
      setStatus("error");
    }
  };

  const fld = "w-full bg-[#0A0A0B] border border-[#2A2B31] px-4 py-3 text-white focus:outline-none focus:border-[#E10600] transition-colors";
  const lbl = "font-mono-tt text-[11px] uppercase tracking-wider text-[#9A9CA3] block mb-1.5";

  if (status === "done") {
    return (
      <div className="pt-36 pb-32 bg-[#0A0A0B] min-h-screen" data-testid="afspraak-success">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <div className="border border-[#E10600] bg-[#E10600]/10 p-10">
            <CheckCircle2 size={56} className="text-[#E10600] mx-auto mb-5" />
            <h1 className="heading-xl text-4xl text-white mb-4">Aanvraag ontvangen!</h1>
            <p className="text-[#B8B9BE] leading-relaxed mb-4">
              Bedankt {f.naam || ""}. We hebben je afspraakaanvraag goed ontvangen en je krijgt een bevestigingsmail.
            </p>
            <div className="flex items-start gap-3 bg-[#0A0A0B] border border-[#2A2B31] p-4 text-left mb-6">
              <Info size={20} className="text-[#E10600] shrink-0 mt-0.5" />
              <p className="text-sm text-[#9A9CA3]">
                <span className="text-white font-600">Belangrijk:</span> je afspraak is pas definitief na bevestiging door TT Motorsports. We nemen zo snel mogelijk contact met je op.
              </p>
            </div>
            <button onClick={() => { setF(init); setStatus("idle"); }} className="text-[#E10600] font-display uppercase text-sm">
              Nieuwe afspraak aanvragen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="afspraak-page">
      <section className="pt-36 pb-12 bg-[#0A0A0B] border-b border-[#2A2B31] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
          <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600] flex items-center gap-2"><CalendarCheck size={14} /> Online afspraak</span>
          <h1 className="heading-xl text-5xl md:text-7xl text-white mt-3">Maak een afspraak</h1>
          <p className="text-[#9A9CA3] text-lg mt-4 max-w-2xl">
            Voor <span className="text-white">alle merken en alle types motoren</span> — van crossmotor tot sport- en touringmotor. Vul het formulier in en we bevestigen je afspraak zo snel mogelijk.
          </p>
        </div>
      </section>

      <section className="section-pad bg-[#0A0A0B]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <form onSubmit={submit} data-testid="afspraak-form" className="space-y-10">
            <input type="text" tabIndex={-1} autoComplete="off" value={f.honeypot} onChange={(e) => set("honeypot", e.target.value)} className="hidden" aria-hidden="true" />

            {/* Stap 1 - gegevens */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 bg-[#E10600] text-white font-display font-800 flex items-center justify-center">1</span>
                <h2 className="heading-xl text-2xl text-white">Jouw gegevens</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>Naam *</label><input data-testid="apt-naam" required value={f.naam} onChange={(e) => set("naam", e.target.value)} className={fld} /></div>
                <div><label className={lbl}>E-mailadres *</label><input data-testid="apt-email" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} className={fld} /></div>
                <div><label className={lbl}>Telefoonnummer *</label><input data-testid="apt-telefoon" type="tel" inputMode="tel" autoComplete="tel" required value={f.telefoon} onChange={(e) => set("telefoon", e.target.value)} className={fld} /></div>
              </div>
            </div>

            {/* Stap 2 - motor */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 bg-[#E10600] text-white font-display font-800 flex items-center justify-center">2</span>
                <h2 className="heading-xl text-2xl text-white">Jouw motor</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Type motor</label>
                  <select data-testid="apt-type" value={f.type_motor} onChange={(e) => set("type_motor", e.target.value)} className={fld}>
                    <option value="">Kies een type...</option>
                    {motorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className={lbl}>Merk</label><input value={f.merk} onChange={(e) => set("merk", e.target.value)} className={fld} placeholder="bv. Yamaha" /></div>
                <div><label className={lbl}>Model</label><input value={f.model} onChange={(e) => set("model", e.target.value)} className={fld} /></div>
                <div><label className={lbl}>Bouwjaar</label><input value={f.bouwjaar} onChange={(e) => set("bouwjaar", e.target.value)} className={fld} /></div>
                <div><label className={lbl}>Nummerplaat (optioneel)</label><input value={f.nummerplaat} onChange={(e) => set("nummerplaat", e.target.value)} className={fld} /></div>
                <div><label className={lbl}>Kilometerstand (optioneel)</label><input inputMode="numeric" value={f.kilometerstand} onChange={(e) => set("kilometerstand", e.target.value)} className={fld} /></div>
              </div>
            </div>

            {/* Stap 3 - dienst */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 bg-[#E10600] text-white font-display font-800 flex items-center justify-center">3</span>
                <h2 className="heading-xl text-2xl text-white">Waarvoor kom je langs? *</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {services.map((s) => (
                  <button key={s} type="button" data-testid={`apt-service-${s.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => set("service", s)}
                    className={`px-3 py-3 text-sm font-600 text-left border transition-colors ${
                      f.service === s ? "bg-[#E10600] border-[#E10600] text-white" : "bg-[#141518] border-[#2A2B31] text-[#B8B9BE] hover:border-[#E10600]/60"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              {f.service === "Andere" && (
                <input data-testid="apt-service-andere" value={f.service_andere} onChange={(e) => set("service_andere", e.target.value)}
                  placeholder="Beschrijf waarvoor je langskomt..." className={`${fld} mt-3`} />
              )}
              <div className="mt-5">
                <label className={lbl}>Ophalen / leveren *</label>
                <div className="grid sm:grid-cols-2 gap-2 max-w-md">
                  {[{ v: false, l: "Ik breng zelf" }, { v: true, l: "Ophalen gewenst" }].map((o) => (
                    <button key={String(o.v)} type="button" data-testid={`apt-ophalen-${o.v}`} onClick={() => set("ophalen", o.v)}
                      className={`px-3 py-2.5 text-sm text-center border transition-colors ${f.ophalen === o.v ? "bg-[#E10600] border-[#E10600] text-white" : "bg-[#141518] border-[#2A2B31] text-[#B8B9BE] hover:border-[#E10600]/60"}`}>{o.l}</button>
                  ))}
                </div>
                {f.ophalen && (
                  <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="apt-ophaal-adres">
                    <div className="sm:col-span-2"><label className={lbl}>Straat + huisnummer *</label><input data-testid="apt-straat" value={f.ophaal_straat} onChange={(e) => set("ophaal_straat", e.target.value)} className={fld} /></div>
                    <div><label className={lbl}>Postcode *</label><input data-testid="apt-postcode" value={f.ophaal_postcode} onChange={(e) => set("ophaal_postcode", e.target.value)} className={fld} /></div>
                    <div><label className={lbl}>Stad / gemeente *</label><input data-testid="apt-stad" value={f.ophaal_stad} onChange={(e) => set("ophaal_stad", e.target.value)} className={fld} /></div>
                  </div>
                )}
              </div>
            </div>

            {/* Stap 4 - moment */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 bg-[#E10600] text-white font-display font-800 flex items-center justify-center">4</span>
                <h2 className="heading-xl text-2xl text-white">Gewenst moment</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Gewenste datum</label>
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="apt-datum-trigger"
                        className={`${fld} flex items-center justify-between text-left`}>
                        <span className={selectedDate ? "text-white capitalize" : "text-[#5A5C63]"}>
                          {selectedDate ? format(selectedDate, "EEEE d MMMM yyyy", { locale: nl }) : "Kies een open dag..."}
                        </span>
                        <CalendarIcon size={16} className="text-[#E10600] shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0 bg-[#141518] border-[#2A2B31]">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={pickDate}
                        weekStartsOn={1}
                        locale={nl}
                        fromDate={today}
                        disabled={[{ before: today }, (date) => !openDays.has(date.getDay()) || isVerlof(date)]}
                        classNames={{
                          caption_label: "text-sm font-medium text-white capitalize",
                          nav_button: "h-7 w-7 bg-transparent p-0 text-white opacity-70 hover:opacity-100 border border-[#2A2B31] rounded",
                          head_cell: "text-[#9A9CA3] rounded-md w-9 font-normal text-[0.75rem]",
                          day: "h-9 w-9 p-0 font-normal text-white hover:bg-[#2A2B31] rounded",
                          day_selected: "bg-[#E10600] text-white hover:bg-[#E10600] focus:bg-[#E10600]",
                          day_today: "border border-[#E10600] text-white",
                          day_disabled: "text-[#5A5C63] opacity-40 line-through",
                        }}
                      />
                      <p className="text-[11px] text-[#9A9CA3] px-3 pb-3 font-mono-tt">Enkel open dagen zijn selecteerbaar.</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className={lbl}>Gewenst tijdstip</label>
                  <select data-testid="apt-tijdstip" value={f.tijdstip} onChange={(e) => set("tijdstip", e.target.value)}
                    disabled={!selectedDate || !f.service || loadingSlots} className={`${fld} disabled:opacity-50 disabled:cursor-not-allowed`}>
                    <option value="">
                      {!f.service ? "Kies eerst een dienst" : !selectedDate ? "Kies eerst een dag" : loadingSlots ? "Laden..." : availableSlots.length === 0 ? "Geen vrije tijdstippen" : "Kies een tijdstip..."}
                    </option>
                    {availableSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {selectedDate && f.service && !loadingSlots && availableSlots.length === 0 && (
                    <p className="text-[11px] text-[#E10600] mt-1.5 font-mono-tt">Geen vrije tijdstippen op deze dag. Kies een andere dag.</p>
                  )}
                  {selectedDate && availableSlots.length > 0 && hoursMap[selectedDate.getDay()] && (
                    <p className="text-[11px] text-[#9A9CA3] mt-1.5 font-mono-tt capitalize">
                      {format(selectedDate, "EEEE", { locale: nl })}: <span className="normal-case">{hoursMap[selectedDate.getDay()]}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4"><label className={lbl}>Extra opmerkingen</label><textarea rows={4} value={f.opmerkingen} onChange={(e) => set("opmerkingen", e.target.value)} className={fld} placeholder="Beschrijf gerust je vraag of het probleem..." /></div>
            </div>

            <div className="flex items-start gap-3 bg-[#141518] border border-[#2A2B31] p-4">
              <Info size={20} className="text-[#E10600] shrink-0 mt-0.5" />
              <p className="text-sm text-[#9A9CA3]">Je afspraak is pas definitief na bevestiging door TT Motorsports. Na verzending krijg je een bevestigingsmail van ontvangst.</p>
            </div>

            {status === "error" && <p className="text-[#E10600]" data-testid="apt-error">{errMsg || "Er ging iets mis. Probeer opnieuw of bel ons op 0485 74 15 24."}</p>}

            <div className="flex flex-wrap gap-4">
              <button data-testid="apt-submit" type="submit" disabled={status === "sending"}
                className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white font-display uppercase font-700 tracking-wide px-8 py-4 flex items-center gap-2 transition-colors">
                {status === "sending" ? "Versturen..." : <>Afspraak aanvragen <ArrowRight size={18} /></>}
              </button>
              <a href="tel:0485741524" className="border border-[#2A2B31] hover:border-white text-white px-8 py-4 flex items-center gap-2 font-display uppercase font-700 transition-colors">
                <Phone size={18} /> Liever bellen?
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
